/* @flow */
/**
 * Redux Toolkit slice for PMO/DEC contact configuration data.
 * Manages configuration rows, filters, dropdown lookups, and create/update API requests.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner, updateDialogStates } from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import { getFilteredResults, getFilterDropdownValuesFromResponse, sortDataAscendingConfig } from '../../helpers/dataHelpers';
import settingsData from '../mock/pmoDecSettingsMock';
import { showForPMODivision } from '../../helpers/envServer';

export const name = 'pmoDecConfig';

function createInitialState() {
  return {
    filterDropdownValues: {
    },
    selectedFilters: {
      factoryCode: [],
      managingOffice: [],
      managingUserID: [],
      managingUserEmail: [],
      managingUser: [],
      ...showForPMODivision && { divisionCode: [] }
    },
    dataId: '',
    settingsFilterId: '',
    SettingsData: [],
    pmoRequestBody: null,
    dataRows: settingsData,
    pmoConfigScreenData: null,
    unfilteredConfigScreenData: null,
    addFactoryDetailDropdownValues: {
      factoryCode: []
    },
    pmoOfficeData: null
  };
}

export const initialState = createInitialState();

/**
 * Reducer that stores PMO configuration results and their unfiltered source rows.
 */
function updatePMOConfigScreen(state: Object, action: Object) {
  // UPDATE_PMO_CONFIG_SCREEN_RESULTS

  // Keep both filtered rows for display and raw rows for clearing filters.
  const payload = {
    data: action.payload.flattenedResponse,
    filteredResults: action.payload.filteredResults || action.payload.flattenedResponse
  };

  return {
    ...state,
    pmoConfigScreenData: payload.filteredResults,
    unfilteredConfigScreenData: payload.data,
    dataId: cryptoRand().toString().substr(2, 8)
  };
}

/**
 * Reducer that resets PMO filters and restores unfiltered configuration results.
 */
function clearPMOFilters(state: Object) {
  // CLEAR_PMO_DEC_FILTERS

  return {
    ...state,
    selectedFilters: {
      ...initialState.selectedFilters
    },
    // Clearing filters restores the last unfiltered API result set.
    pmoConfigScreenData: state.unfilteredConfigScreenData
  };
}

/**
 * Reducer that stores filtered PMO configuration rows and refreshes the filter id.
 */
function updateFilteredPMOConfigScreen(state: Object, action: Object) {
  // UPDATE_PMO_DEC_FILTERED_RESULTS

  const payload = {
    data: action.payload
  };

  return {
    ...state,
    pmoConfigScreenData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Reducer that updates one selected PMO filter value array.
 */
function updateFilterValues(state: Object, action: Object) {
  // ADD_PMO_DEC_FILTER_VALUES

  // Ignore malformed filter payloads so existing filter state remains unchanged.
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

/**
 * Reducer that stores sorted PMO filter dropdown values.
 */
function updateFilterDropdownValues(state: Object, action: Object) {
  // SETTINGS_UPDATE_FILTER_DROPDOWN_VALUES

  if (
    action.payload && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortDataAscendingConfig(action.payload)
    };
  }
  return { ...state };
}

/**
 * Reducer that refreshes the PMO filter request id used to notify the UI.
 */
function updatePMORequestFilterId(state: Object) {
  // SET_SETTINGS_FILTER_ID

  return { ...state, settingsFilterId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Reducer that stores factory dropdown values for adding PMO factory details.
 */
function setFactoryCodeDropdown(state: Object, action: Object) {
  // SET_PMO_FACTORY_CODE_DROPDOWN_VALUES

  return {
    ...state,
    addFactoryDetailDropdownValues: {
      ...state.addFactoryDetailDropdownValues,
      factoryCode: action.payload
    }
  };
}

/**
 * Reducer that stores PMO office vendor data returned by the office lookup API.
 */
function setOfficeCodeDropdownValues(state: Object, action: Object) {
  // SET_PMO_OFFICE_CODE_DROPDOWN_VALUES

  return {
    ...state,
    pmoOfficeData: action.payload?.pmoVendorList
  };
}

/**
 * Builds the reducer map consumed by the PMO/DEC configuration slice.
 */
function createReducers() {
  return {
    updatePMOConfigScreen,
    clearPMOFilters,
    updateFilteredPMOConfigScreen,
    updateFilterValues,
    updateFilterDropdownValues,
    updatePMORequestFilterId,
    setFactoryCodeDropdown,
    setOfficeCodeDropdownValues
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that calls pmoContacts, flattens rows, applies filters, and updates dropdowns.
 */
function getPMOConfigResults() {
  return createAsyncThunk(
    `${name}/getPMOConfigResults`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'pmoContacts',
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          const state = getState();
          // Flatten nested API rows before filtering and rendering the configuration table.
          const flattenedResponse = getflattenResponse(response.data);
          const { selectedFilters } = state.pmodecLookup;
          const filteredResults = getFilteredResults(flattenedResponse.objects, selectedFilters, 'pmoDec');
          dispatch(actions.updatePMOConfigScreen(
            { flattenedResponse: flattenedResponse.objects, filteredResults }
          ));
          dispatch(actions.updateFilterDropdownValues(
            getFilterDropdownValuesFromResponse(flattenedResponse.objects, [
              'factoryCode',
              'managingOffice',
              'managingUserID',
              'managingUser',
              'managingUserEmail',
              // Request division filter values only when the division feature flag is enabled.
              showForPMODivision && 'divisionCode'
            ].filter(Boolean), 'pmoDec')
          ));
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          console.log(error);
          callback(error);
        });
    }
  );
}

/**
 * Async thunk that calls dropdownLookup and stores factory options for add-factory flows.
 */
function fetchPMOConfigAddFactoryDropDownData() {
  return createAsyncThunk(
    `${name}/fetchPMOConfigAddFactoryDropDownData`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      request(
        {
          api: 'dropdownLookup',
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          const { partnerNumber } = response.data;
          // Convert lookup partner numbers into dropdown text/value pairs.
          const factoryCodeList = partnerNumber.map
            ? partnerNumber.map((option) => ({
              text: option.partnerNo,
              value: option.partnerNo
            }))
            : [];
          dispatch(actions.setFactoryCodeDropdown(factoryCodeList));
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Async thunk that calls pmoOfficeData and stores PMO office vendor code data.
 */
function getPMOOfficeData() {
  return createAsyncThunk(
    `${name}/getPMOOfficeData`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      dispatch(updateDialogStates({ getPMOVendorCodesSpinner: true }));
      request(
        {
          api: 'pmoOfficeData',
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(updateDialogStates({ getPMOVendorCodesSpinner: false }));
          dispatch(actions.setOfficeCodeDropdownValues(response.data));
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk that posts new PMO factory contact details to pmoContacts.
 */
function addNewFactoryDetails() {
  return createAsyncThunk(
    `${name}/addNewFactoryDetails`,
    async (
      {
        data,
        callback
      },
      { getState, dispatch }
    ) => {
      // Dropdown selections store objects, but the API expects scalar factory and division codes.
      const updatedData = data.map((row) => {
        const { value: factoryCode } = row.factoryCode;
        if (row.factoryCode) {
          if (showForPMODivision) {
            const { value: divisionCode } = row.divisionCode;
            return ({
              ...row,
              factoryCode,
              divisionCode
            });
          }
          return {
            ...row,
            factoryCode
          };
        }
        return row;
      });
      const pmoRequestBody = {
        pmoData: updatedData
      };
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'pmoContacts',
          method: 'post',
          data: pmoRequestBody,
          cancellable: true
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk that sends PMO contact updates to pmoContacts with type update.
 */
function updatePMOEmail() {
  return createAsyncThunk(
    `${name}/updatePMOEmail`,
    async (
      {
        data,
        callback
      },
      { getState, dispatch }
    ) => {
      const updateRequestBody = {
        type: 'update',
        pmoData: data
      };
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'pmoContacts',
          method: 'put',
          data: updateRequestBody,
          cancellable: true
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk that updates PMO selected filters and recalculates filtered screen results.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (
      { payload },
      { getState, dispatch }
    ) => {
      try {
        dispatch(actions.updateFilterValues(payload));
        const state = getState();
        const { selectedFilters, unfilteredConfigScreenData } = state.pmodecLookup;
        // Reapply filters against the unfiltered data set to avoid cumulative filtering.
        const filteredResults = getFilteredResults(unfilteredConfigScreenData, selectedFilters, 'pmoDec');
        dispatch(actions.updateFilteredPMOConfigScreen(filteredResults));
      } catch (error) {
        console.log(error);
      }
    }
  );
}

/**
 * Async thunk that dispatches PMO filter reset state.
 */
function clearFilters() {
  return createAsyncThunk(
    `${name}/clearFilters`,
    async (_, { dispatch }) => {
      dispatch(actions.clearPMOFilters());
    }
  );
}

/**
 * Creates async thunk action creators exposed by the PMO/DEC configuration slice.
 */
function createExtraActions() {
  return {
    getPMOConfigResults: getPMOConfigResults(),
    fetchPMOConfigAddFactoryDropDownData: fetchPMOConfigAddFactoryDropDownData(),
    getPMOOfficeData: getPMOOfficeData(),
    addNewFactoryDetails: addNewFactoryDetails(),
    updatePMOEmail: updatePMOEmail(),
    applyFilters: applyFilters(),
    clearFilters: clearFilters()
  };
}

export const extraActions = createExtraActions();

export const pmoDecConfigActions = { ...actions, ...extraActions };
export const pmoDecConfigReducer = slice.reducer;
